import {Injectable} from '@angular/core';
import {Observable, ReplaySubject} from 'rxjs';
import {PlatformFamily} from '@webmate-web-core/shared/types/devices/device-properties';
import {ProjectId} from '@webmate-web-core/shared/types/ids';
import {Platform} from '@webmate-web-core/shared/types/platform';
import {PersistenceService} from '@webmate-web-core/shared/services/persistence.service';

export interface FavoriteDevice {
    id: string;
    platform?: Platform;
    platformType?: string;
    manufacturerName?: string;
    modelName?: string;
}

@Injectable({
    providedIn: 'root'
})
export class FavoriteDeviceService {

    readonly MAX_FAVORITE_DEVICES_PER_KEY = 4;
    private favoriteDevicesByKey$: Map<string, ReplaySubject<FavoriteDevice[]>> = new Map();

    constructor(private persistenceService: PersistenceService) {}

    getFavoriteDevices(platformFamily: PlatformFamily, projectId: ProjectId): FavoriteDevice[] {
        const stored = this.persistenceService.getObjectItem(this.getFavoriteDevicesKey(platformFamily, projectId));
        return Array.isArray(stored) ? stored as FavoriteDevice[] : [];
    }

    getFavoriteDevices$(platformFamily: PlatformFamily, projectId: ProjectId): Observable<FavoriteDevice[]> {
        const key = this.getFavoriteDevicesKey(platformFamily, projectId);
        const subject = this.getFavoriteDevicesSubject(key);
        subject.next(this.getFavoriteDevices(platformFamily, projectId));
        return subject.asObservable();
    }

    isFavoriteDevice(platformFamily: PlatformFamily, projectId: ProjectId, deviceOfferId: string): boolean {
        return this.getFavoriteDevices(platformFamily, projectId).some(fav => fav.id === deviceOfferId);
    }

    storeFavoriteDevice(platformFamily: PlatformFamily, projectId: ProjectId, favorite: FavoriteDevice) {
        const favorites = this.getFavoriteDevices(platformFamily, projectId);
        const index = favorites.findIndex(fav => fav.id === favorite.id);

        if (index > -1) {
            favorites.splice(index, 1);
        } else {
            if (favorites.length >= this.MAX_FAVORITE_DEVICES_PER_KEY) {
                favorites.shift();
            }
            favorites.push(favorite);
        }

        const key = this.getFavoriteDevicesKey(platformFamily, projectId);
        this.persistenceService.setObjectItem(key, favorites);
        this.getFavoriteDevicesSubject(key).next(favorites);
    }

    private getFavoriteDevicesKey(platformFamily: PlatformFamily, projectId: ProjectId): string {
        return `favoriteDevices:${platformFamily}:${projectId}`;
    }

    private getFavoriteDevicesSubject(key: string): ReplaySubject<FavoriteDevice[]> {
        let subject = this.favoriteDevicesByKey$.get(key);
        if (!subject) {
            subject = new ReplaySubject<FavoriteDevice[]>(1);
            this.favoriteDevicesByKey$.set(key, subject);
        }
        return subject;
    }
}
